<template>
  <NuxtLayout
    name="generic"
    :header="isFormSubmitted ? 'standardDark' : data?.gateHeader"
    :footer="isFormSubmitted ? 'standard' : data?.gateFooter"
  >
    <div
      v-show="isFormSubmitted"
      :class="{
        'video-template mt-14 bg-www-color-neutral-off-white md:mt-120px': true,
      }"
    >
      <template v-if="data">
        <BreadCrumbFromRoute
          :title="data.title"
          :class="{ 'container mx-auto': true }"
        />

        <VideoHero :data />

        <VideoMeta :data />
        <CTABanner
          v-if="data.CTABanner?.reference"
          :data="data.CTABanner.reference"
        />
        <SuggestedContent
          v-if="!data.removeSuggestedContent"
          :suggested-title="data.suggestedContentTitle"
          :current-id="data._id"
        />
      </template>
    </div>
    <div
      v-show="!isFormSubmitted"
      id="gated-form"
    >
      <HeroContent
        v-if="data?.gateHeroContent"
        v-bind="data.gateHeroContent"
        title=""
      />
      <section
        :class="{
          'bg-www-color-neutral-very-light': true,
        }"
      >
        <div
          :class="{
            container: true,
          }"
        >
          <div
            :class="{
              'grid grid-cols-12 gap-4': true,
            }"
          >
            <div
              :class="{
                'col-span-12 md:col-span-7': true,
              }"
            >
              <PortableText
                v-if="data?.gateBodyContent?.footerPortableText"
                :portable-data="data.gateBodyContent.footerPortableText"
                :class="{
                  'text-www-color-neutral-medium-dark': true,
                }"
              />
            </div>
            <div
              :class="{
                'col-span-12 md:col-span-5': true,
              }"
            >
              <SectionBuilderForm
                v-if="data?.gateForm"
                v-bind="data.gateForm"
                :class="{
                  'mt-8 bg-www-color-white lg:p-30px': true,
                }"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import BreadCrumbFromRoute from '~/components/Atoms/BreadCrumb/BreadCrumbFromRoute.vue';
import VideoHero from '~/components/Organisms/VideoHero/VideoHero.vue';
import VideoMeta from '~/components/Organisms/VideoHero/VideoMeta.vue';
import CTABanner from '~/components/Organisms/CTABanner/CTABanner.vue';
import SuggestedContent from '~/components/Organisms/SuggestedContent/SuggestedContent.vue';
import HeroContent from '~/components/Atoms/HeroContent/HeroContent.vue';
import PortableText from '~/components/Atoms/PortableText/PortableText.vue';
import SectionBuilderForm from '~/components/Molecules/Form/SectionBuilderForm.vue';
import { useHekateEventBus } from '~/composables/useHekateEventBus';
import { queryVideo } from '~/queries/pages/PageVideo.query';
import type { QueryVideoResult } from '~/sanity/types';
import { extractAbstractFromPortableText } from '~/utils/extractBreakoutSummary';

const { path: currentPathname } = useRoute();

const { data } = await useWwwSanityQueryNonNull<QueryVideoResult>(queryVideo, {
  currentPathname,
});

// Check if the page is a gated form
const isFormSubmitted = ref(!data.value?.useGate);
const { addEventListener } = useHekateEventBus();

onMounted(() => {
  addEventListener('form-successfully-submitted', () => {
    isFormSubmitted.value = true;
  });
});

usePageHead({
  _id: data.value?._id,
  author: data.value?.person?.[0]?.reference?.name,
  description: data.value?.ogDescription || data.value?.description,
  ogImage: data.value?.ogImage?.asset || data.value?.cardImage?.asset,
  excludeFromExternalSearch: data.value?.excludeFromExternalSearch,
  title: data.value?.htmlTitle || data.value?.title,
  twitterCard: 'summary',
  structuredData: [
    {
      schemaType: 'CreativeWork',
      title: data.value?.htmlTitle || data.value?.title,
      description: data.value?.ogDescription || data.value?.description,
      abstract: extractAbstractFromPortableText(data.value?.content?.videoPortableText),
      enabled: true,
    },
  ],
});
</script>
